import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const typeClasses: Record<ToastType, string> = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

export function Toast({ message, onDismiss }: { message: ToastMessage; onDismiss: () => void }) {
  return (
    <div className={`flex items-center gap-3 rounded-lg border p-4 shadow-md ${typeClasses[message.type]}`}>
      <p className="text-sm flex-1">{message.message}</p>
      <button onClick={onDismiss} className="text-current opacity-50 hover:opacity-100" aria-label="Dismiss">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

let nextId = 0;

const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  /**
   * Every pending auto-dismiss timer, so it can be cancelled.
   *
   * The auto-dismiss used to be a bare `setTimeout(...)` whose handle was discarded, which made it
   * uncancellable by construction. A `ToastProvider` that unmounted with a non-error toast still on
   * screen — a route change, a logout, a portal teardown — left a timer that fired up to 5s later
   * and called `setToasts` on a component that no longer existed.
   *
   * In a browser React 18 absorbs that as a no-op, so it was invisible in the product. Under
   * Vitest it is fatal: the timer outlives the test that created it, fires after jsdom is torn
   * down, and React's `getCurrentEventPriority` dereferences `window` — `ReferenceError: window is
   * not defined`, reported as an unhandled error. Vitest then exits 1 even though every test
   * passed. Seen on build 11796 (PR !8710): "Test Files 263 passed / Tests 3810 passed", process
   * exit 1, originating in `admin-portal/src/pages/my-workspace/EmailTriagePanel.test.tsx` — a file
   * that has nothing to do with toasts and could not have fixed it locally.
   *
   * It is timing-dependent, so it reddens an arbitrary PR rather than the one that caused it.
   */
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: number) => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback((type: ToastType, message: string) => {
    const id = ++nextId;
    setToasts(prev => [...prev, { id, type, message }]);
    if (type !== 'error') {
      const handle = setTimeout(() => {
        timers.current.delete(id);
        setToasts(prev => prev.filter(t => t.id !== id));
      }, AUTO_DISMISS_MS);
      timers.current.set(id, handle);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Cancel first: a manual dismiss otherwise leaves the auto-dismiss timer pending for a toast
      // that is already gone, which is the same leak by a shorter route.
      clearTimer(id);
      setToasts(prev => prev.filter(t => t.id !== id));
    },
    [clearTimer],
  );

  useEffect(() => {
    // Read the ref during the effect, not in the cleanup: by teardown `timers.current` could in
    // principle have been reassigned, and the cleanup must cancel the map it was set up against.
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2 w-96">
        {toasts.map(t => (
          <Toast key={t.id} message={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
